import './ui/styles.css';

import { h, clear, mount, initials, type Child } from './ui/dom.js';
import { button, toast, openModal, field, input, loadingState, appFooter } from './ui/components.js';
import { Router } from './router.js';
import type { Page, PageContext } from './page.js';
import {
  getSession,
  loadStoredSession,
  setSession,
  onSessionChange,
  getUser,
  can,
} from './store.js';
import { api, ApiError, setUnauthorizedHandler, setAuthToken } from './api.js';
import { ROLE_LABELS, type Session } from './types.js';

import { loginPage } from './views/login.js';
import { dashboardPage } from './views/dashboard.js';
import { templatesPage } from './views/templates.js';
import { templateDetailPage } from './views/template-detail.js';
import { editionsPage } from './views/editions.js';
import { editionDetailPage } from './views/edition-detail.js';
import { agendaPage } from './views/agenda.js';
import { checklistPage } from './views/checklist.js';
import { supportPage } from './views/support.js';
import { myMinistrationsPage } from './views/my-ministrations.js';
import { ministrationPage } from './views/ministration.js';
import { pulpitPage } from './views/pulpit.js';
import { usersPage } from './views/users.js';
import { auditPage } from './views/audit.js';

const appRoot = document.getElementById('app');
if (!appRoot) throw new Error('Elemento #app nao encontrado.');

const router = new Router();
let currentPage: Page | null = null;

/* --------------------------------------------------------------------------
   Tema (claro / escuro), persistido por dispositivo.
   -------------------------------------------------------------------------- */
const THEME_KEY = 'f2f.theme';

function applyTheme(theme: string): void {
  document.documentElement.dataset.theme = theme;
}

function initTheme(): void {
  const stored = localStorage.getItem(THEME_KEY);
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  applyTheme(stored ?? (prefersDark ? 'dark' : 'light'));
}

function toggleTheme(): void {
  const next = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
  applyTheme(next);
  localStorage.setItem(THEME_KEY, next);
}

/* --------------------------------------------------------------------------
   Navegacao lateral
   -------------------------------------------------------------------------- */
interface NavItem {
  path: string;
  label: string;
  icon: string;
  visible: () => boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

function navGroups(): NavGroup[] {
  return [
    {
      label: 'Operação',
      items: [
        { path: '/', label: 'Painel', icon: '◈', visible: () => true },
        {
          path: '/minhas-ministracoes',
          label: 'Minhas Ministrações',
          icon: '✦',
          visible: () => can.isMinistrador(),
        },
        { path: '/edicoes', label: 'Edições', icon: '▤', visible: () => true },
        { path: '/modelos', label: 'Modelos Mestre', icon: '⧉', visible: () => can.viewTemplates() },
      ],
    },
    {
      label: 'Gestão',
      items: [
        { path: '/usuarios', label: 'Usuários', icon: '☰', visible: () => can.manageUsers() },
        { path: '/auditoria', label: 'Auditoria', icon: '◷', visible: () => can.viewAudit() },
      ],
    },
  ];
}

function renderSidebar(): HTMLElement {
  const user = getUser();
  const current = window.location.hash.replace(/^#/, '') || '/';

  const groups = navGroups()
    .map((group) => {
      const items = group.items.filter((item) => item.visible());
      if (items.length === 0) return null;

      return h(
        'div',
        { class: 'sidebar__group' },
        h('div', { class: 'sidebar__label', text: group.label }),
        ...items.map((item) => {
          const active =
            item.path === '/'
              ? current === '/' || current === ''
              : current.startsWith(item.path);
          return h(
            'button',
            {
              class: 'nav-link',
              ...(active ? { 'aria-current': 'page' } : {}),
              onclick: () => router.navigate(item.path),
            },
            h('span', { class: 'nav-link__icon', text: item.icon }),
            item.label,
          );
        }),
      );
    })
    .filter(Boolean);

  return h(
    'aside',
    { class: 'sidebar' },
    h(
      'div',
      { class: 'sidebar__brand' },
      h('div', { class: 'sidebar__mark', text: 'FF' }),
      h(
        'div',
        null,
        h('div', { class: 'sidebar__title', text: 'Face a Face' }),
        h('div', { class: 'sidebar__subtitle', text: 'Ministrações · V2' }),
      ),
    ),
    h('nav', { class: 'sidebar__group' }, ...groups),
    h(
      'div',
      { class: 'sidebar__user' },
      h(
        'div',
        { class: 'row' },
        h(
          'div',
          {
            class: 'list__index',
            style: { background: 'rgb(212 162 74 / 18%)', color: '#d4a24a' },
            text: user ? initials(user.name) : '?',
          },
        ),
        h(
          'div',
          { style: { minWidth: '0' } },
          h('div', { class: 'sidebar__user-name', text: user?.name ?? '' }),
          h('div', { class: 'sidebar__user-role', text: user ? ROLE_LABELS[user.role] : '' }),
        ),
      ),
      h(
        'div',
        { class: 'row' },
        button('Minha senha', {
          variant: 'ghost',
          size: 'sm',
          onClick: () => openPasswordModal(),
        }),
        button('Sair', { variant: 'ghost', size: 'sm', onClick: () => signOut() }),
      ),
    ),
  );
}

/* --------------------------------------------------------------------------
   Modal de troca de senha
   -------------------------------------------------------------------------- */
function openPasswordModal(): void {
  const currentInput = input({ type: 'password', placeholder: '••••••••' });
  const newInput = input({ type: 'password', placeholder: 'mínimo 6 caracteres' });
  const errorSlot = h('div', { class: 'field__error' });

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    try {
      await api.post('/api/auth/password', {
        currentPassword: currentInput.value,
        newPassword: newInput.value,
      });
      modal.close();
      toast.success('Senha atualizada', 'Use a nova senha no próximo acesso.');
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao alterar a senha.';
    }
  };

  const modal = openModal({
    title: 'Alterar minha senha',
    body: [
      field({ label: 'Senha atual', control: currentInput, required: true }),
      field({ label: 'Nova senha', control: newInput, required: true }),
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal.close() }),
      button('Salvar', { onClick: () => void submit() }),
    ],
  });
}

/* --------------------------------------------------------------------------
   Layout e ciclo de renderizacao
   -------------------------------------------------------------------------- */
function renderShell(container: Element): { topbar: HTMLElement; content: HTMLElement } {
  clear(container);
  const topbar = h('header', { class: 'topbar' });
  const content = h('div', { class: 'content' });
  container.append(
    h(
      'div',
      { class: 'shell' },
      renderSidebar(),
      h('div', { class: 'main' }, topbar, content, appFooter()),
    ),
  );
  return { topbar, content };
}

function renderTopbar(topbar: HTMLElement, page: Page): void {
  const actions: Child[] = [...(page.actions ?? [])];
  actions.push(
    button(document.documentElement.dataset.theme === 'dark' ? '☀' : '☾', {
      variant: 'ghost',
      size: 'sm',
      onClick: () => {
        toggleTheme();
        if (currentPage) renderTopbar(topbar, currentPage);
      },
      'aria-label': 'Alternar tema',
    }),
  );

  clear(topbar);
  topbar.append(
    h(
      'div',
      { class: 'topbar__heading' },
      h('h1', { class: 'topbar__title', text: page.title }),
      page.subtitle ? h('div', { class: 'topbar__subtitle', text: page.subtitle }) : null,
    ),
    h('div', { class: 'topbar__actions' }, ...actions),
  );
}

function renderFullBleed(page: Page): void {
  clear(appRoot as Element);
  (appRoot as Element).append(...(Array.isArray(page.body) ? page.body : [page.body]) as Node[]);
}

async function renderPage(renderer: (context: PageContext) => Promise<Page>, context: PageContext): Promise<void> {
  try {
    const page = await renderer(context);
    currentPage = page;

    if (page.fullBleed) {
      renderFullBleed(page);
      return;
    }

    const { topbar, content } = renderShell(appRoot as Element);
    renderTopbar(topbar, page);
    mount(content, ...(Array.isArray(page.body) ? page.body : [page.body]) as Child[]);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      handleUnauthorized();
      return;
    }
    const { topbar, content } = renderShell(appRoot as Element);
    renderTopbar(topbar, { title: 'Algo deu errado', body: null });
    mount(
      content,
      h(
        'div',
        { class: 'card' },
        h(
          'div',
          { class: 'card__body' },
          h('div', { text: error instanceof Error ? error.message : 'Erro inesperado.' }),
          h(
            'div',
            { style: { marginTop: '16px' } },
            button('Voltar ao painel', { onClick: () => router.navigate('/') }),
          ),
        ),
      ),
    );
  }
}

/* --------------------------------------------------------------------------
   Sessao
   -------------------------------------------------------------------------- */
function signOut(): void {
  setSession(null);
  toast.info('Sessão encerrada.');
  router.navigate('/entrar', { replace: true });
}

function handleUnauthorized(): void {
  setSession(null);
  if (!window.location.hash.startsWith('#/entrar')) {
    toast.error('Sessão expirada', 'Entre novamente para continuar.');
    router.navigate('/entrar', { replace: true });
  }
}

function boot(): void {
  initTheme();
  setUnauthorizedHandler(handleUnauthorized);
  loadStoredSession();

  onSessionChange((session: Session | null) => {
    if (!session && !window.location.hash.startsWith('#/entrar')) {
      router.navigate('/entrar', { replace: true });
    }
  });

  const requireAuth = (renderer: (context: PageContext) => Promise<Page>) => {
    return async (context: PageContext): Promise<void> => {
      if (!getSession()) {
        router.navigate('/entrar', { replace: true });
        return;
      }
      await renderPage(renderer, context);
    };
  };

  router
    .add('/entrar', async () => {
      await renderPage(loginPage, { params: {}, query: new URLSearchParams(), navigate: (p) => router.navigate(p) });
    })
    .add('/', requireAuth(dashboardPage))
    .add('/modelos', requireAuth(templatesPage))
    .add('/modelos/:id', requireAuth(templateDetailPage))
    .add('/edicoes', requireAuth(editionsPage))
    .add('/edicoes/:id', requireAuth(editionDetailPage))
    .add('/edicoes/:id/agenda', requireAuth(agendaPage))
    .add('/edicoes/:id/prontidao', requireAuth(checklistPage))
    .add('/edicoes/:id/apoio', requireAuth(supportPage))
    .add('/minhas-ministracoes', requireAuth(myMinistrationsPage))
    .add('/ministracoes/:id', requireAuth(ministrationPage))
    .add('/ministracoes/:id/pulpito', requireAuth(pulpitPage))
    .add('/usuarios', requireAuth(usersPage))
    .add('/auditoria', requireAuth(auditPage))
    .setFallback(async () => {
      if (getSession()) {
        router.navigate('/', { replace: true });
      } else {
        router.navigate('/entrar', { replace: true });
      }
    })
    .setNotFound(async () => {
      const { topbar, content } = renderShell(appRoot as Element);
      renderTopbar(topbar, { title: 'Página não encontrada', body: null });
      mount(
        content,
        h(
          'div',
          { class: 'card' },
          h(
            'div',
            { class: 'card__body' },
            h('div', { text: 'O endereço acessado não existe neste sistema.' }),
            h(
              'div',
              { style: { marginTop: '16px' } },
              button('Ir para o painel', { onClick: () => router.navigate('/') }),
            ),
          ),
        ),
      );
    });

  if (!window.location.hash) {
    router.navigate(getSession() ? '/' : '/entrar', { replace: true });
  }

  // Revalida a sessao armazenada em segundo plano.
  if (getSession()) {
    api
      .get<{ user: unknown; permissions: unknown }>('/api/auth/me')
      .catch(() => undefined)
      .then((result) => {
        if (result) {
          const session = getSession();
          if (session) {
            setSession({
              ...session,
              user: result.user as Session['user'],
              permissions: result.permissions as Session['permissions'],
            });
          }
        }
      });
  }

  const splash = appRoot as Element;
  clear(splash);
  splash.append(loadingState('Carregando o sistema…'));

  router.start();
}

void setAuthToken;
void boot();
